import React from 'react'

/**
 * NotificationModal - Modal bloqueante para notificaciones de seguimiento
 *
 * Se muestra cuando hay una notificacion "exact" (hora de contactar al prospecto).
 * Muestra la info importante del prospecto y dos botones:
 *  - Postponer 15 minutos
 *  - Ir al contacto (navega a TodoList y enfoca la fila)
 */
export default function NotificationModal({ notification, onSnooze, onGoToContact, onDismiss }) {
  if (!notification) return null

  const contactName = notification.first_name
    ? `${notification.first_name} ${notification.last_name || ''}`.trim()
    : notification.contacto_nombre || notification.nombre || notification.contacto_phone

  const timeStr = notification.scheduled_at?.split('T')[1]?.substring(0, 5) || ''
  const dateStr = notification.scheduled_at?.split('T')[0] || ''

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={() => onDismiss && onDismiss(notification.id)}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-surface-dark-alt rounded-3xl shadow-2xl w-full max-w-2xl overflow-hidden animate-slide-in-right">
        {/* Header con gradiente rojo */}
        <div className="bg-gradient-to-br from-red-500 via-red-600 to-red-700 px-8 py-7 relative">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
              <svg className="w-9 h-9 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
            </div>
            <div className="flex-1">
              <div className="text-sm font-bold uppercase tracking-wider text-red-100">Hora de contactar</div>
              <div className="text-4xl font-bold text-white">{timeStr || 'AHORA'}</div>
            </div>
            <button
              onClick={() => onDismiss && onDismiss(notification.id)}
              className="text-white/70 hover:text-white text-3xl leading-none p-1"
              title="Cerrar"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Body - info del prospecto */}
        <div className="px-8 py-6 space-y-4">
          <div>
            <div className="text-sm font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">Prospecto</div>
            <div className="text-3xl font-bold text-gray-900 dark:text-gray-100 leading-tight">
              {contactName}
            </div>
            <div className="text-lg text-gray-500 dark:text-gray-400 font-medium">
              {notification.contacto_phone}
            </div>
          </div>

          {notification.hint && (
            <div className="border-l-4 border-amber-400 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 rounded-r-lg">
              <div className="text-sm font-semibold uppercase text-amber-700 dark:text-amber-300 mb-0.5">Pista</div>
              <div className="text-lg text-gray-800 dark:text-gray-200">{notification.hint}</div>
            </div>
          )}

          {notification.message_text && !notification.hint && (
            <div className="text-sm text-gray-700 dark:text-gray-300 italic">
              {notification.message_text}
            </div>
          )}
        </div>

        {/* Footer - botones */}
        <div className="px-8 pb-8 pt-3 flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => onSnooze && onSnooze(notification.id, 15)}
            className="flex-1 px-4 py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Posponer 15 min
          </button>
          <button
            onClick={() => onSnooze && onSnooze(notification.id, 30)}
            className="flex-1 px-4 py-4 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl text-lg font-semibold hover:bg-gray-200 dark:hover:bg-gray-600 transition flex items-center justify-center gap-2"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Posponer 30 min
          </button>
          <button
            onClick={() => onGoToContact && onGoToContact(notification)}
            className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl text-lg font-semibold hover:from-blue-700 hover:to-blue-800 transition shadow-md flex items-center justify-center gap-2"
          >
            Ir al contacto
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M14 5l7 7m0 0l-7 7m7-7H3" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  )
}
